import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

/**
 * MUI's `Divider`, which the donor theme never overrides — so MUI's own
 * default *is* the source spec (`docs/agents/migration-guidelines.md`, "Shared
 * donor components port as shared Atlas components").
 *
 * The donor draws every rule on the Item Catalog's cards and rows with it:
 * `<Divider orientation='horizontal' flexItem />` between a card's picture and
 * its text (`items-card-list/item-card.tsx:218`), and
 * `<Divider orientation='vertical' flexItem />` twice in a list row — after the
 * image column and before the description (`items-data-list/item-data-row.tsx:198`,
 * `:227`).
 *
 * Two things the vendored `Separator` does not give on its own, and the reason
 * this wrapper exists rather than classes at each call site:
 *
 * - **The colour.** `Separator` paints `bg-border`, an *opaque*
 *   `oklch(0.922 0 0)` = #e5e5e5. MUI's is `palette.divider`, a translucent
 *   `rgba(0, 0, 0, 0.12)` that darkens whatever it crosses — #e0e0e0 over a
 *   white card. A shade apart, and measurably wrong on a surface that draws 37
 *   of these on a default 20-row page.
 * - **`flexItem` on a vertical rule.** `Separator`'s vertical variant is
 *   `h-full`, which resolves against a flex parent of indefinite height and
 *   collapses to nothing. MUI's `flexItem` is `align-self: stretch` with
 *   `height: auto`, which is what makes the rule take the row's height.
 *
 * - **The a11y tree.** `Separator` defaults `decorative` to `true`, publishing
 *   `role="none"`. MUI's divider is not decorative: with no children it
 *   renders a horizontal rule as an `<hr>`, whose implicit role is
 *   `separator`, and a vertical one as `<div role="separator"
 *   aria-orientation="vertical">` (`Divider.js:206`, `:208`, `:230`). So the
 *   donor publishes 37 separators on a default card page where the primitive's
 *   default publishes none. Radix's element is a `div` in both orientations
 *   rather than an `<hr>`, which changes nothing a screen reader consumes —
 *   the role is what it reads, and `aria-orientation` follows the same rule
 *   MUI uses: set on the vertical, omitted on the horizontal default.
 *
 * `flexItem` defaults to `true` here: both of the donor's usages pass it, and a
 * vertical rule without it is the collapsed one nobody wants. Pass
 * `flexItem={false}` for a rule inside a definite-height box. `decorative`
 * likewise defaults to MUI's behaviour rather than the primitive's; pass
 * `decorative` for a rule that genuinely is just paint.
 */
export function MuiDivider({
  orientation = "horizontal",
  flexItem = true,
  decorative = false,
  className,
  ...props
}: React.ComponentProps<typeof Separator> & { flexItem?: boolean }) {
  return (
    <Separator
      orientation={orientation}
      decorative={decorative}
      className={cn(
        "bg-lennar-divider",
        // `data-[orientation=vertical]:h-auto`, matching the primitive's own
        // variant rather than a bare `h-auto`: tailwind-merge keys a variant
        // class separately, so an unprefixed utility does not replace
        // `data-[orientation=vertical]:h-full` — it loses to it on specificity
        // and the rule collapses to nothing.
        orientation === "vertical" &&
          flexItem &&
          "w-px self-stretch data-[orientation=vertical]:h-auto",
        className,
      )}
      {...props}
    />
  );
}
